import { useEffect, useRef, useState, type FC } from 'react';
import { Button, Link, Space, Switchbox, Text, Textarea } from '@g2u/ui-kit';
import { Card, Col, Form, Popconfirm, Row } from 'antd';
import { PlusCircleOutlined, RedoOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';

import { JobServiceType, Role, type JsonDataItem, type WithOptionalDataTest } from 'types';
import { OW_FREE_TV_INSTALL_SKU } from 'helpers/constants';
import {
  getTaskLogs,
  getTaskNotes,
  putTaskReopen,
  postVoipCall,
  postTaskNote,
  postCheckTaskLock,
  getTask,
} from 'queries';
import { TaskNotesAndLogs } from 'components';
import { CloseCallbackModal, CallbackActions } from '../components';
import { Toast, parseErrors } from 'helpers';
import { BookingStore, RootStore } from 'store';
import styles from '../callbacks.module.scss';

type Props = {
  record: JsonDataItem;
  onReopen: () => void;
  onClose: () => void;
  onLink: (payload: JsonDataItem) => void;
  onLock: (payload: JsonDataItem) => void;
  onUnlock: (payload: JsonDataItem) => void;
  page?: number;
  linkNewCustomerId?: number;
} & WithOptionalDataTest;

export const CallbackDetails: FC<Props> = observer((props) => {
  const { record, onReopen, onClose, onLink, page, linkNewCustomerId, onLock, onUnlock } = props;
  const { setCustomer, setIsNewCustomer, setCallback, setProductSelection } = BookingStore;
  const { user } = RootStore;
  const [showLogs, setShowLogs] = useState<boolean>(false);
  const [showCloseCallbackModal, setShowCloseCallbackModal] = useState<boolean>(false);
  const [closeStep, setCloseStep] = useState<number>(0);
  const isRemoveLockAvailable = useRef<boolean>(true);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const isCallbackClosed = !!record.archive_datetime;

  useEffect(() => {
    getTask({ status: isCallbackClosed ? 0 : 1, taskId: record.ID });
  }, []);

  // Technician or customer call
  const { mutate: startCall, isLoading: isCalling } = useMutation(postVoipCall, {
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  // Reopen Callback
  const { mutate: reopenCallback, isLoading: isCallbackReopening } = useMutation(putTaskReopen, {
    onSuccess: () => {
      onReopen();
      Toast.show({ description: `Callback ${record.record_ID} has been reopened.`, type: Toast.Type.Success });
      Toast.show({ description: 'A new note has been added.', type: Toast.Type.Success });
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  // Notes and Logs
  const {
    mutate: getNotes,
    data: taskNotes,
    isLoading: isNotesLoading,
  } = useMutation(getTaskNotes, {
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });
  const {
    mutate: getLogs,
    data: taskLogs,
    isLoading: isLogsLoading,
  } = useMutation(getTaskLogs, {
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });
  const { mutate: addNote, isLoading: addingNote } = useMutation(postTaskNote, {
    onSuccess: () => {
      Toast.show({ description: 'A new note has been added.', type: Toast.Type.Success });
      getNotes({ status: Number(!record.archive_datetime), taskId: record.ID });
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  // Lock check
  const {
    mutate: checkLock,
    data: lockData,
    isLoading: checkingLockStatus,
  } = useMutation(postCheckTaskLock, {
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const handleFormChange = (changedField: JsonDataItem) => {
    if (Object.keys(changedField).includes('showLogs')) {
      setShowLogs(changedField.showLogs);
      getLogs({ status: Number(!record.archive_datetime), taskId: record.ID });
    }
  };

  const handleStartBooking = () => {
    const customer = record.task_links?.customer;
    setCallback(record);
    if (record.record?.ow_sale?.sku === OW_FREE_TV_INSTALL_SKU) {
      setProductSelection({ serviceType: JobServiceType.Promotions });
    }
    if (customer) {
      setCustomer(customer);
    } else {
      setIsNewCustomer(true);
    }
    navigate('/booking/location');
  };

  const handleCreateCustomerAndCloseCallback = () => {
    navigate('/customer/create', {
      state: { callbackId: record.ID, page },
    });
  };

  const handleCloseCallback = () => {
    setCloseStep(0);
    setShowCloseCallbackModal(true);
  };

  const handleLeaveMessageAndCloseCallback = () => {
    setCloseStep(1);
    setShowCloseCallbackModal(true);
  };

  const handleCloseAction = () => {
    isRemoveLockAvailable.current = false;
    onClose();
  };

  const handleStartCall = () => {
    const numberToCall = record.task_links?.customer?.contact[0]?.replace(/\s/g, '') || record.record.phone;
    numberToCall
      ? startCall(numberToCall)
      : Toast.show({
          description: 'No phone number in file',
          type: Toast.Type.Error,
        });
  };

  useEffect(() => {
    // need to close a callback with a message if new customer has been created and linked
    linkNewCustomerId && handleCloseCallback();
  }, [linkNewCustomerId]);

  useEffect(() => {
    getNotes({ status: Number(!record.archive_datetime), taskId: record.ID });
    if (!isCallbackClosed) {
      onLock({ taskId: record.ID });
      checkLock({ taskId: record.ID });
    }
    return () => {
      isRemoveLockAvailable.current && !isCallbackClosed && onUnlock({ taskId: record.ID });
    };
  }, []);

  const isAllowedToViewLogs = user?.role && user.role !== Role.CSS;

  return (
    <div className={styles.callbacks__details}>
      <div className={styles.callbacks__detailsColumn}>
        <Card className={styles.callbacks__detailsSection}>
          <Text.$ style={{ whiteSpace: 'pre-line' }}>{record.record?.note}</Text.$>
        </Card>
        <Row gutter={16} style={{ marginTop: 16 }}>
          <Col xs={24}>
            {!isCallbackClosed ? (
              <CallbackActions
                isLoading={isCalling || checkingLockStatus}
                lockedBy={lockData?.data[record.ID]?.hard_lock ? lockData?.data[record.ID].user.name : undefined}
                isLinked={!!record.task_links?.customer}
                isPrepaidHoursPackageProduct={record.record?.prepaid_hours_package_product}
                onCall={handleStartCall}
                onStartBooking={handleStartBooking}
                onClose={handleCloseCallback}
                onCloseWithMessage={handleLeaveMessageAndCloseCallback}
                onCloseWithCreate={handleCreateCustomerAndCloseCallback}
                onLink={(payload: JsonDataItem) => {
                  return onLink({ taskId: record.ID, ...payload });
                }}
                callbackId={record.ID}
                callbackType={record.record.source}
              />
            ) : (
              <Popconfirm
                title="Reopen Callback"
                disabled={!!record.record?.ow_sale?.record_id}
                onConfirm={() => {
                  reopenCallback({ taskId: record.ID });
                }}
                description="Are you sure you want to reopen this callback?"
                okText="Yes"
              >
                <div>
                  <Button.$
                    disabled={!!record.record?.ow_sale?.record_id}
                    dataTestId="reopen"
                    onClick={() => null}
                    loading={isCallbackReopening}
                  >
                    <RedoOutlined /> Reopen
                  </Button.$>
                </div>
              </Popconfirm>
            )}
          </Col>
        </Row>
      </div>
      <Form
        form={form}
        layout="vertical"
        colon={false}
        onValuesChange={handleFormChange}
        className={styles.callbacks__detailsColumn}
      >
        <Card
          actions={[
            <>
              {!isCallbackClosed && (
                <Popconfirm
                  key="add"
                  title="Add New Note"
                  icon={null}
                  onConfirm={() => {
                    addNote({ note: form.getFieldValue('newNote'), taskId: record.ID });
                    form.setFieldValue('newNote', undefined);
                  }}
                  description={
                    <Textarea.$
                      name="newNote"
                      placeholder="Text area"
                      style={{ width: 276, minHeight: 150, maxHeight: 150 }}
                    />
                  }
                  okText="Save"
                >
                  <div>
                    <Link.$ className={styles.callbacks__action} disabled={!!record.archive_datetime || addingNote}>
                      <PlusCircleOutlined className={styles.callbacks__actionIcon} /> Add New Note
                    </Link.$>
                  </div>
                </Popconfirm>
              )}
            </>,
            isAllowedToViewLogs ? (
              <Space.$ direction={Space.Direction.Horizontal} justify={Space.Justify.Center} key="logs">
                <Switchbox.$ name="showLogs" size="small" label="Show Logs" colon={false} horizontal />
              </Space.$>
            ) : null,
          ]}
        >
          <TaskNotesAndLogs
            notes={taskNotes?.data}
            logs={taskLogs?.data}
            isLoading={isLogsLoading || isNotesLoading}
            showLogs={showLogs}
          />
        </Card>
      </Form>
      <CloseCallbackModal
        step={closeStep}
        record={record}
        isOpen={showCloseCallbackModal}
        onOk={() => {
          setShowCloseCallbackModal(false);
          handleCloseAction();
        }}
        onCancel={() => setShowCloseCallbackModal(false)}
        onStepChange={setCloseStep}
      />
    </div>
  );
});
